import { useEffect, useState } from 'react';
import { Button, Card } from '@atoms/index';
import type { OrderCardOrder } from '@molecules/OrderCard';
import { CANCELLATION_REASON_LABELS } from '@utils/formatters';

const CANCEL_REASONS = [
  { value: '', label: 'Sebep seçin (isteğe bağlı)' },
  ...Object.entries(CANCELLATION_REASON_LABELS).map(([value, label]) => ({ value, label })),
];

export function CancelOrderModal(props: {
  isOpen: boolean;
  order: OrderCardOrder | null;
  isSubmitting: boolean;
  onConfirm: (reason?: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  useEffect(() => {
    if (!props.isOpen) {
      setReason('');
      setCustomReason('');
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [props.isOpen, props.onClose]);

  if (!props.isOpen) return null;

  const resolvedReason = reason === 'other' ? customReason.trim() : reason;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      <button className="absolute inset-0 bg-black/50" onClick={props.onClose} aria-label="Modal kapat" />
      <Card className="relative z-[10000] w-full max-w-md p-0 overflow-hidden">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Siparişi iptal et</h3>
          <p className="text-gray-600 mb-4">
            <strong>{props.order?.restaurant?.name ?? 'Sipariş'}</strong> numaralı siparişi iptal etmek istediğinize emin misiniz?
            Hazırlanmaya başlanan siparişler iptal edilemez.
          </p>
          {props.order?.paymentMethod === 'CREDIT_CARD' && (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
              Kart ile ödendi. İptal sonrası <strong>iade 5–10 iş günü</strong> içinde aynı karta yansıyacaktır.
            </p>
          )}
          <div className="mb-6 space-y-2">
            <label className="block text-sm font-medium text-gray-700">İptal sebebi (isteğe bağlı)</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input-base input-placeholder w-full"
              disabled={props.isSubmitting}
            >
              {CANCEL_REASONS.map((r) => (
                <option key={r.value || 'none'} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            {reason === 'other' && (
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Sebebinizi yazın..."
                className="input-base input-placeholder w-full min-h-[80px] resize-y"
                maxLength={500}
                disabled={props.isSubmitting}
              />
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" className="w-full" onClick={props.onClose} disabled={props.isSubmitting}>
              Vazgeç
            </Button>
            <Button
              className="w-full bg-red-600 hover:bg-red-700"
              onClick={() => props.onConfirm(resolvedReason || undefined)}
              isLoading={props.isSubmitting}
            >
              Siparişi iptal et
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
